/**
 * Operational hierarchy widget — spec §F5.8, as the 2026-08-22 client review (R5) rebuilt it.
 *
 * ## What went, and why
 *
 * §F5.8 specified five rows — Countries, Regions, Communes, Watersheds, Partnerships — each
 * with a ☑ visibility checkbox and a ⦿ `name=replvl` radio, plus a caption explaining which
 * was which. R5: *"remove the 'View data by:' block (the 5 checkbox/radio rows) entirely.
 * Admin boundaries display by default. The 'Filter by:' cascade governs display + data +
 * filter (reporting level derives from the cascade)."*
 *
 * The rows had become a second geography control standing next to the cascade and quietly
 * disagreeing with it. A filter already unticked the rows outside itself and disabled them
 * (`visibilityForScope`), already narrowed the live radios to the levels its subtree had
 * units at (`scopeLevels`), and already re-checked a radio when it forced the level. What was
 * left for the reader to do with ten controls was to discover which four of them the filter
 * had not already decided — so the cascade decides all of them, and this widget is the
 * cascade plus a line saying what it derived.
 *
 * ## What the level is now
 *
 * A pure function of the scope, and it lives in the store where the scope does:
 *
 * | filter                | reporting level                     | boundaries drawn            |
 * |-----------------------|-------------------------------------|-----------------------------|
 * | none                  | regions (`NO_SCOPE_LEVEL`)          | countries + regions         |
 * | a country             | its regions                         | countries + regions         |
 * | a region              | its communes                        | that region + its communes  |
 * | a commune             | itself                              | that commune                |
 * | a watershed           | itself, or its partnerships (§M3.5) | watersheds + partnerships   |
 * | a partnership         | itself                              | partnerships                |
 *
 * — `setScope`/`clearScope` (`levelWithinScope`, `NO_SCOPE_LEVEL`) for the level, and
 * `useHierarchyStore.applyScope` (`visibilityForScope`) for the boundaries. Both were already
 * written and already driving the map; R5 deleted the controls that could contradict them.
 *
 * **§M3.5 survives intact.** `'watershed'` is still a *conditional* mode — MELS reports
 * B.2.1/C.2.1 per `watershed_id`, and an indicator without native watershed values must not
 * be painted at that stratum — so a watershed scope reports at watershed level when the
 * mapped indicator has those values and at partnership level when it does not. That is the
 * `reconcileReportingLevel` effect below, which is now the widget's only behaviour: it is
 * still the one component that reads both halves (the level, and the catalogue row that says
 * whether the level is meaningful).
 *
 * **One capability went with the radios**, and it was worth saying out loud: there was no
 * longer a way to *ask* for the country-level choropleth (4 polygons) or for the
 * programme-wide watershed one (11), because no cascade selection implies them. Both
 * remained valid store states and a `lv=` permalink still restored them — but nothing in the
 * interface could reach them (docs/NOTES.md).
 *
 * ## What came back, and why it is not the radios again — client observation P1
 *
 * The 2025 MELS baseline made that gap the client's headline bug: 58 survey estimates stored
 * at country and watershed level, `aggregation: 'none'` so §E4 will not roll them anywhere
 * else, and a cascade that sends every unfiltered view to **regions**. Nothing painted. Two
 * repairs, in the store and here:
 *
 * * the level **snaps** onto a stratum the mapped indicator actually has (`snapLevelToData`,
 *   run from the effect below on every indicator, scope and country change). That is the fix
 *   — a cold map with a baseline indicator paints the four country polygons;
 * * and where an indicator has *more than one* reachable stratum — the baseline has countries
 *   **and** watersheds programme-wide — a two-button `<LevelChooser>` sits under the cascade.
 *
 * It is not the five radios. Those offered every level for every indicator and stood next to
 * a cascade that had already decided; this offers **only levels that have data**, only when
 * there is more than one of them, and only for `aggregation: 'none'` series (`chooserLevels`).
 * For all 27 roll-up indicators nothing renders and the level stays cascade-derived, exactly
 * as R5 left it.
 *
 * **Why here and not beside the legend title.** The legend describes what *is* painted; this
 * decides what *will be*. Every other control that answers "which geography?" — the five
 * cascade rungs, "Clear filter" — is in this widget, and the level is the sixth rung of that
 * same question: putting it in the legend would split one decision across two panels and give
 * the reader two places to look for why the map changed. It also lands exactly where the R5
 * radios were, which is where a returning reader goes.
 */
import { useEffect, useMemo } from 'react';
import { isCompactViewport } from '../../lib/viewport';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { Widget } from '../../components/Widget';
import { fetchIndicators, queryKeys } from '../../lib/api';
import { pendingPermalinkIds } from '../../lib/permalink';
import { effectiveLevels, useAppStore } from '../../lib/state';
import { CascadeFilter } from './CascadeFilter';
import { LevelChooser } from './LevelChooser';

export function OperationalHierarchy() {
  const { t } = useTranslation();
  const indicatorId = useAppStore((s) => s.indicator);
  const reconcileReportingLevel = useAppStore((s) => s.reconcileReportingLevel);
  /**
   * P1(b): the level is a function of the cascade *and* of the indicator, so a geography
   * change has to re-ask. `setScope`/`setCountry`/`clearScope` cannot — they are in
   * `lib/state.ts`, which knows nothing of `/indicators` — so they derive the cascade's level
   * as they always did and this effect corrects it in the same tick.
   */
  const scope = useAppStore((s) => s.scope);
  const country = useAppStore((s) => s.country);

  const { data: indicators } = useQuery({
    queryKey: queryKeys.indicators(),
    queryFn: fetchIndicators,
  });
  // A reference into the query's own array, so it is stable between renders and safe as an
  // effect dependency — which is what lets `available` be memoised on it rather than
  // rebuilt (and re-firing the effect) every time anything in this widget re-renders.
  const meta = indicators?.find((i) => i.id === indicatorId) ?? null;
  const watershedReady = Boolean(indicatorId && meta?.has_watershed_values);
  const available = useMemo(
    () => effectiveLevels(indicatorId ? meta : null),
    [indicatorId, meta],
  );

  /**
   * §M3.5's catch-all: a permalink hydrates `reportingLevel` with `setState` before
   * `/indicators` exists, and `PermalinkSync` can drop the indicator a stale link named. This
   * is the one place that knows both halves — the level and the catalogue — so it reconciles
   * them once the catalogue has actually answered (`indicators` undefined would demote every
   * restored `lv=watershed` link on boot).
   *
   * **It waits for `PermalinkSync` to finish first** (R6 fix). Both effects fire off the same
   * `/indicators` landing, and this one renders with whatever indicator the *hash* named —
   * which `PermalinkSync` may be about to replace, because the link is stale or names a
   * demo-only row. Reconciling against that indicator demotes the level for an indicator that
   * is on its way out, and the demotion is one-way: `PermalinkSync`'s own later, correct call
   * has no path back up to `'watershed'`. So while a restored hash still has unconfirmed
   * indicator ids, the answer belongs to the component that is confirming them; it reconciles
   * in the same tick, and this effect re-runs on the corrected indicator immediately after.
   * (`pendingPermalinkIds` is empty on every load that carried no `ind=`/`ov=`/`biv=`, so
   * outside a permalink boot this gate is not there.)
   */
  useEffect(() => {
    if (!indicators) return;
    if (pendingPermalinkIds().indicators.length) return;
    reconcileReportingLevel(indicatorId, watershedReady, available);
    // `scope` and `country` are dependencies rather than passengers: the snap is against the
    // level the *cascade* has just derived, so a filter change must re-run it. They are read
    // from the store inside `set`, which is why neither is passed as an argument.
  }, [
    indicators,
    indicatorId,
    watershedReady,
    available,
    scope,
    country,
    reconcileReportingLevel,
  ]);

  // The derived-level statement that used to render here was removed on client request
  // (2026-08-27), with the pane's "mapped at" hint. The chooser below is not its return:
  // that line *described* the level, this one offers a choice, and only where the mapped
  // indicator has two strata with data in them (see the header).
  return (
    <Widget
      id="boundsWidget"
      title={t('hierarchy.title')}
      icon="basemap"
      defaultCollapsed={isCompactViewport()}
    >
      <div id="bwBody">
        <CascadeFilter />
        <LevelChooser meta={meta} />
      </div>
    </Widget>
  );
}

export default OperationalHierarchy;
